import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetPurchaseNoteQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function PurchaseNotePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: note, isLoading, error } = useGetPurchaseNoteQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();

  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: company?.currencyDecimals ?? 2,
        maximumFractionDigits: company?.currencyDecimals ?? 2,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(company?.currencyDecimals ?? 2)}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  useEffect(() => {
    // If needed, we can trigger automatic print, but usually manual button is preferred.
  }, []);

  if (isLoading) return <Typography sx={{ p: 4 }}>Loading...</Typography>;
  if (error || !note) return <Typography color="error" sx={{ p: 4 }}>Error loading purchase note</Typography>;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', py: 4, '@media print': { bgcolor: 'transparent', py: 0 } }}>
      {/* Action Toolbar */}
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          '@media print': { display: 'none' },
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ borderRadius: 2 }}
        >
          Print Purchase Note
        </Button>
      </Box>

      {/* Main Printable Document */}
      <Paper
        elevation={1}
        sx={{
          maxWidth: 800,
          mx: 'auto',
          p: 6,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          '@media print': {
            boxShadow: 'none',
            p: 0,
            mx: 0,
            maxWidth: '100%',
          },
        }}
      >
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            {company?.logoUrl && (
              <Box
                component="img"
                src={company.logoUrl}
                alt="Logo"
                sx={{
                  height: 60,
                  width: 'auto',
                  maxWidth: 140,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                {company?.name || 'Company Name'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', fontSize: 13, lineHeight: 1.4 }}>
                  {company.address}
                </Typography>
              )}
              {company?.email && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                  Email: {company.email}
                </Typography>
              )}
              {company?.phone && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                  Phone: {company.phone}
                </Typography>
              )}
              {company?.trn && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, fontWeight: 600, mt: 0.5 }}>
                  TRN: {company.trn}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: 'primary.main',
                textTransform: 'uppercase',
                letterSpacing: 1,
                mb: 1,
              }}
            >
              Purchase Note
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Note #: {note.noteNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Date: {formatDate(note.noteDate)}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label={note.status}
                color={
                  note.status === 'converted'
                    ? 'success'
                    : note.status === 'cancelled'
                    ? 'error'
                    : 'default'
                }
                size="small"
                sx={{ textTransform: 'capitalize', fontWeight: 700 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Addresses Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
              Supplier Details
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              {note.supplier?.name}
            </Typography>
            {note.supplier?.address && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', fontSize: 13, mb: 0.5 }}>
                {note.supplier.address}
              </Typography>
            )}
            {note.supplier?.phone && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Phone: {note.supplier.phone}
              </Typography>
            )}
            {note.supplier?.email && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                Email: {note.supplier.email}
              </Typography>
            )}
            {(note.supplier?.taxId || note.supplier?.gstin) && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
                Tax/VAT ID: {note.supplier.taxId || note.supplier.gstin}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1, textAlign: 'right' }}>
            {/* Can add shipping/billing or specific details here if required */}
          </Box>
        </Box>

        {/* Items Table */}
        <Table size="small" sx={{ mt: 3 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, width: '8%', py: 1.5 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Product / Item</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: '15%', py: 1.5 }}>Quantity</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: '18%', py: 1.5 }}>Unit Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: '20%', py: 1.5 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(note.items || []).map((item, idx) => (
              <TableRow key={item.id || idx}>
                <TableCell sx={{ py: 1.5 }}>{idx + 1}</TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                    {item.productName || item.product?.name || 'Unknown Item'}
                  </Typography>
                  {item.product?.sku && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      SKU: {item.product.sku}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontSize: 13 }}>{Number(item.quantity).toFixed(2)}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontSize: 13 }}>{formatAmount(item.unitPrice)}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, fontSize: 13 }}>
                  {formatAmount(Number(item.quantity) * Number(item.unitPrice))}
                </TableCell>
              </TableRow>
            ))}
            {(!note.items || note.items.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No items in this purchase note</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Summary Block */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Box sx={{ width: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatAmount(note.subtotal)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Grand Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {formatAmount(note.grandTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Notes/Terms */}
        {note.notes && (
          <Box sx={{ mt: 6, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Notes / Remarks
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'text.primary', fontSize: 13, lineHeight: 1.5 }}>
              {note.notes}
            </Typography>
          </Box>
        )}

        {/* Signatures / Footer */}
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-between', pt: 6 }}>
          <Box sx={{ width: 200, textAlign: 'center' }}>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Prepared By
            </Typography>
            {note.user && (
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {note.user.firstName} {note.user.lastName}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: 200, textAlign: 'center' }}>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Authorized Signature
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default PurchaseNotePrint;
